import classNames from "classnames";
import {FaEllipsis} from "react-icons/fa6";
import {FiChevronDown, FiEye, FiEyeOff} from "react-icons/fi";

import {Accordion} from "@/components/accordion/Accordion.tsx";
import {DropdownItem} from "@/components/dropdown/subComponents/DropdownItem/DropdownItem.tsx";
import {DropdownButton} from "@/components/dropdownButton/DropdownButton.tsx";
import {FormLabel} from "@/components/form/FormLabel.tsx";
import {FormSubtext} from "@/components/formSubtext/FormSubtext.tsx";
import {IconButton} from "@/components/iconButton/IconButton.tsx";
import {Select} from "@/components/select/Select.tsx";
import {SelectOption} from "@/components/select/Select.types.ts";
import {EllipsisText} from "@/components/typography/EllipsisText.tsx";
import {CheckInfo} from "@/lib/queries/statuspages.ts";
import {METRIC_AGGREGATIONS, MetricAggregation, MetricsExplorerSchema} from "@/lib/types/metricsExplorer.ts";
import {GuessedField} from "@/pages/metricsExplorer/catalogDefaults.ts";
import {ExplorerSeriesConfig, MAX_SERIES} from "@/pages/metricsExplorer/explorerConfig.ts";
import {
  availableDataTypes,
  availableDimensions,
  availableMetrics,
  findDataType,
  pruneSelections,
} from "@/pages/metricsExplorer/schemaUtils.ts";
import {CheckMultiSelect} from "@/pages/metricsExplorer/seriesBuilder/CheckMultiSelect.tsx";
import {DimensionFilterSelect} from "@/pages/metricsExplorer/seriesBuilder/DimensionFilterSelect.tsx";

import styles from "./SeriesSpecRow.module.scss";

interface SeriesSpecRowProps {
  series: ExplorerSeriesConfig;
  /** Names of the series' checks resolved so far, for the collapsed summary. */
  checkNames: string[];
  letter: string;
  /** Fields the catalog filled by a guess, flagged until the user sets them. */
  guessedFields: GuessedField[];
  isExpanded: boolean;
  canDuplicate: boolean;
  onToggleExpanded: () => void;
  schema: MetricsExplorerSchema | undefined;
  checkTypes: string[];
  start: Date;
  end: Date;
  onChange: (patch: Partial<ExplorerSeriesConfig>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onChecksResolved: (checks: CheckInfo[]) => void;
}

const AGGREGATION_LABELS: Record<MetricAggregation, string> = {
  AVG: "Average",
  MIN: "Minimum",
  MAX: "Maximum",
  SUM: "Sum",
  COUNT: "Count",
  MEDIAN: "Median",
  P75: "75th percentile",
  P90: "90th percentile",
  P95: "95th percentile",
  P99: "99th percentile",
};

const toOption = (value: string, label: string): SelectOption => ({value, label});

const GUESSED_HINT = "Picked for these checks.";

export const SeriesSpecRow = ({
  series,
  checkNames,
  letter,
  guessedFields,
  isExpanded,
  canDuplicate,
  onToggleExpanded,
  schema,
  checkTypes,
  start,
  end,
  onChange,
  onRemove,
  onDuplicate,
  onChecksResolved,
}: SeriesSpecRowProps) => {
  const dataTypes = availableDataTypes(schema, checkTypes);
  const selectedDataType = findDataType(schema, checkTypes, series.dataType);
  const metrics = availableMetrics(selectedDataType);
  const dimensions = availableDimensions(selectedDataType);
  const mixedCheckTypes = checkTypes.length > 1 && dataTypes.length === 0;

  // Splitting by a dimension implies wanting to choose which of its values are drawn, so
  // those come first; below them sit the dimensions filtered without being split on.
  const filterOnlyDimensions = Object.keys(series.filters).filter((name) => !series.groupBy.includes(name));
  const filterableDimensions = [...series.groupBy, ...filterOnlyDimensions];
  const isDataTypeGuessed = guessedFields.includes("dataType");
  const isMetricsGuessed = guessedFields.includes("metrics");
  const metricLabels = series.metrics.map((name) => metrics.find((metric) => metric.name === name)?.label ?? name);

  let title = checkNames.join(", ");
  if (series.checkIds.length === 0) {
    title = "No checks selected";
  } else if (checkNames.length < series.checkIds.length) {
    title = `${series.checkIds.length} checks`;
  }
  const subtitle =
    metricLabels.length > 0
      ? `${AGGREGATION_LABELS[series.aggregation]} · ${metricLabels.join(", ")}`
      : "No metric selected";

  const fieldId = (field: string) => `series-${series.ref}-${field}`;
  const dimensionLabel = (name: string) => dimensions.find((dimension) => dimension.name === name)?.label ?? name;

  const handleChecksChange = (checkIds: number[]) => {
    onChange({checkIds});
  };

  const handleFilterDimensionsChange = (names: string[]) => {
    // The split-by dimensions' own values are not this select's to drop; only the rows
    // added here are rebuilt, and one that survives keeps what was already picked.
    const next: Record<string, string[]> = {};
    series.groupBy.forEach((name) => {
      if (series.filters[name]) {
        next[name] = series.filters[name];
      }
    });
    names.forEach((name) => {
      next[name] = series.filters[name] ?? [];
    });
    onChange({filters: next});
  };

  const handleFilterValuesChange = (dimension: string, values: string[]) => {
    onChange({filters: {...series.filters, [dimension]: values}});
  };

  const handleDataTypeChange = (dataTypeName: string) => {
    // Metrics and dimensions belong to a table; carrying them across would build a query
    // the backend rejects.
    const next = findDataType(schema, checkTypes, dataTypeName);
    const pruned = pruneSelections(next, series.metrics, series.groupBy, series.filters);
    onChange({dataType: dataTypeName, ...pruned});
  };

  return (
    <Accordion.Item eventKey={series.ref}>
      <div className={classNames(styles.row, {[styles.hidden]: series.hidden})} data-series-ref={series.ref}>
        <div className={styles.header}>
          <span className={styles.letter} aria-label={`Series ${letter}`}>
            {letter}
          </span>
          <div className={styles.summary}>
            <EllipsisText className={styles.title}>{title}</EllipsisText>
            <EllipsisText className={styles.subtitle}>{subtitle}</EllipsisText>
          </div>
          <div className={styles.actions}>
            <IconButton
              icon={FiChevronDown}
              variant="ghost"
              className={classNames(styles.toggle, {[styles.toggleOpen]: isExpanded})}
              title={isExpanded ? "Collapse series" : "Expand series"}
              aria-expanded={isExpanded}
              onClick={onToggleExpanded}
            />
            <IconButton
              icon={series.hidden ? FiEyeOff : FiEye}
              variant="ghost"
              title={series.hidden ? "Show on chart" : "Hide from chart"}
              aria-pressed={series.hidden}
              onClick={() => onChange({hidden: !series.hidden})}
            />
            <DropdownButton
              as={IconButton}
              icon={FaEllipsis}
              variant="ghost"
              aria-label="Series actions"
              menuProps={{portal: true}}
            >
              <DropdownItem onClick={onDuplicate} disabled={!canDuplicate}>
                {canDuplicate ? "Duplicate" : `Duplicate (up to ${MAX_SERIES} series)`}
              </DropdownItem>
              <DropdownItem onClick={onRemove}>Remove</DropdownItem>
            </DropdownButton>
          </div>
        </div>

        {/* Collapsed rather than unmounted: the check picker resolves check names and types,
          which the summary and the catalog defaults depend on, even while the row is closed. */}
        <Accordion.Body className={styles.body}>
          <div>
            <FormLabel forId={fieldId("checks")}>Checks</FormLabel>
            <CheckMultiSelect
              inputId={fieldId("checks")}
              value={series.checkIds}
              onChange={handleChecksChange}
              onChecksResolved={onChecksResolved}
              schema={schema}
              checkTypes={checkTypes}
            />
            {mixedCheckTypes && (
              <FormSubtext error="These check types collect no metric in common. Put them in separate series." />
            )}
          </div>

          <div className={styles.fields}>
            <div>
              <FormLabel forId={fieldId("data-type")}>Data</FormLabel>
              <Select
                inputId={fieldId("data-type")}
                size="small"
                options={dataTypes.map((dataType) => toOption(dataType.name, dataType.name))}
                value={series.dataType ? toOption(series.dataType, series.dataType) : null}
                className={classNames({[styles.guessed]: isDataTypeGuessed})}
                onChange={(option) => handleDataTypeChange((option as SelectOption | null)?.value ?? "")}
                isDisabled={dataTypes.length === 0}
                placeholder={checkTypes.length === 0 ? "Select a check first" : "Select data…"}
              />
              {isDataTypeGuessed && <FormSubtext subText={GUESSED_HINT} />}
            </div>

            <div>
              <FormLabel forId={fieldId("aggregation")}>Aggregation</FormLabel>
              <Select
                inputId={fieldId("aggregation")}
                size="small"
                options={METRIC_AGGREGATIONS.map((value) => toOption(value, AGGREGATION_LABELS[value]))}
                value={toOption(series.aggregation, AGGREGATION_LABELS[series.aggregation])}
                onChange={(option) =>
                  onChange({aggregation: ((option as SelectOption | null)?.value ?? "AVG") as MetricAggregation})
                }
              />
            </div>

            <div className={styles.fullWidth}>
              <FormLabel forId={fieldId("metrics")}>Metrics</FormLabel>
              <Select<SelectOption, true>
                inputId={fieldId("metrics")}
                isMulti
                size="small"
                options={metrics.map((metric) => toOption(metric.name, metric.label))}
                value={series.metrics.map((name) =>
                  toOption(name, metrics.find((metric) => metric.name === name)?.label ?? name)
                )}
                className={classNames({[styles.guessed]: isMetricsGuessed})}
                onChange={(options) => onChange({metrics: [...(options ?? [])].map((option) => option.value)})}
                isDisabled={metrics.length === 0}
                placeholder="Select metrics…"
              />
              {isMetricsGuessed && <FormSubtext subText={GUESSED_HINT} />}
            </div>

            <div className={styles.fullWidth}>
              <FormLabel forId={fieldId("group-by")}>Split by</FormLabel>
              <Select<SelectOption, true>
                inputId={fieldId("group-by")}
                isMulti
                size="small"
                options={dimensions.map((dimension) => toOption(dimension.name, dimension.label))}
                value={series.groupBy.map((name) =>
                  toOption(name, dimensions.find((dimension) => dimension.name === name)?.label ?? name)
                )}
                onChange={(options) => onChange({groupBy: [...(options ?? [])].map((option) => option.value)})}
                isDisabled={dimensions.length === 0}
                placeholder="Don't split"
              />
              <FormSubtext subText="One line per value, e.g. per location or per step." />
            </div>

            <div className={styles.fullWidth}>
              <FormLabel forId={fieldId("filter-by")}>Filter by</FormLabel>
              <Select<SelectOption, true>
                inputId={fieldId("filter-by")}
                isMulti
                size="small"
                options={dimensions
                  .filter((dimension) => !series.groupBy.includes(dimension.name))
                  .map((dimension) => toOption(dimension.name, dimension.label))}
                value={filterOnlyDimensions.map((name) => toOption(name, dimensionLabel(name)))}
                onChange={(options) => handleFilterDimensionsChange([...(options ?? [])].map((option) => option.value))}
                isDisabled={dimensions.length === 0}
                placeholder="Filter on another dimension"
              />
              <FormSubtext subText="Narrows the series without splitting it." />
            </div>

            {filterableDimensions.length > 0 && (
              <div className={styles.filters}>
                {filterableDimensions.map((name) => (
                  <DimensionFilterSelect
                    key={name}
                    fieldId={fieldId(`filter-${name}`)}
                    checkIds={series.checkIds}
                    dataType={series.dataType}
                    dimension={name}
                    label={dimensionLabel(name)}
                    value={series.filters[name] ?? []}
                    start={start}
                    end={end}
                    onChange={(values) => handleFilterValuesChange(name, values)}
                  />
                ))}
              </div>
            )}
          </div>
        </Accordion.Body>
      </div>
    </Accordion.Item>
  );
};
